import {inject, Injectable} from "@angular/core";
import {HttpClient} from "@angular/common/http";
import {UrlBuilder} from "../item/url.item";
import {WrapperDto} from "../dto/wrapper.dto";
import {AgreementDto} from "../dto/agreement.dto";
import {UserDto} from "../dto/user.dto";
import {map} from "rxjs/operators";
import {StorageService} from "../../services/storage/storage.service";
import {EmailDto} from "../dto/email.dto";
import {VoidDto} from "../dto/void.dto";
import {SettingDto} from "../dto/setting.dto";
import {OrgUserDto} from "../dto/org-user.dto";
import {OverviewDto} from "../dto/overview-dto";
import {Observable} from "rxjs";
import {UrlDto} from "../item/url.dto";

@Injectable({providedIn: 'root'})
export class UserRepo {

  private readonly userSettingStorageKey: string = 'user:settings';
  private readonly userRead: string = '/v1/user/{id}';
  private readonly userUpdate: string = this.userRead;
  private readonly userSettingsRead: string = '/v1/user/{id}/settings';
  private readonly userSettingsUpdate: string = '/v1/user/{id}/settings';
  private readonly userAgreementsRead: string = '/v1/user/self/agreements';
  private readonly userPing: string = '/v1/user/ping';
  private readonly emailAllRead: string = '/v1/user/{id}/email';
  private readonly emailCreate: string = '/v1/user/{id}/email';
  private readonly emailDelete: string = '/v1/user/{id}/email/delete';
  private readonly assignedOrgsRead: string = '/v1/user/{id}/org/assigned';
  private readonly userInviteAccept: string = '/v1/user/{id}/invite/accept';
  private readonly overviewRead: string = '/v1/user/{id}/overview';

  private readonly http: HttpClient = inject(HttpClient);
  private readonly storageService: StorageService = inject(StorageService);

  public readSelf(): Observable<WrapperDto<UserDto>> {
    const url: UrlDto = UrlBuilder.builder().addPath(this.userRead).addParam('id', 'self').build();
    return this.http.get<WrapperDto<UserDto>>(url.getUrl(), url.getOptions())
      .pipe(map((res) => new WrapperDto(res)));
  }

  public updateUser(user: UserDto): Observable<WrapperDto<UserDto>> {
    const url: UrlDto = UrlBuilder.builder().addPath(this.userUpdate).addParam('id', 'self').build();
    return this.http.post<WrapperDto<UserDto>>(url.getUrl(), user, url.getOptions())
      .pipe(map((res) => new WrapperDto(res)));
  }

  // ##########################################################################
  // #### Ping
  // ##########################################################################

  public ping(): Observable<WrapperDto<VoidDto>> {
    const url: UrlDto = UrlBuilder.builder().addPath(this.userPing).build();
    return this.http.get<WrapperDto<VoidDto>>(url.getUrl(), url.getOptions())
      .pipe(map((res) => new WrapperDto(res)));
  }

  // ##########################################################################
  // ##### User > OrgUser > Org
  // ##########################################################################

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  public readAssignedOrgs(_user: UserDto): Observable<WrapperDto<OrgUserDto>> {
    const url: UrlDto = UrlBuilder.builder().addPath(this.assignedOrgsRead).addParam('id', 'self').build();
    return this.http.get<WrapperDto<OrgUserDto>>(url.getUrl(), url.getOptions())
      .pipe(map((res) => new WrapperDto(res)));
  }

  public inviteAccept(orgUser: OrgUserDto): Observable<WrapperDto<OrgUserDto>> {
    const url: UrlDto = UrlBuilder.builder().addPath(this.userInviteAccept).addParam('id', 'self').build();
    return this.http.post<WrapperDto<OrgUserDto>>(url.getUrl(), orgUser, url.getOptions())
      .pipe(map((res) => new WrapperDto(res)));
  }

  // ##########################################################################
  // #### Settings
  // ##########################################################################

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  public readSettings(_user: UserDto): Observable<WrapperDto<SettingDto>> {
    const url: UrlDto = UrlBuilder.builder().addPath(this.userSettingsRead).addParam('id', 'self').build();
    return this.storageService.cache(this.userSettingStorageKey, () =>
      this.http.get<WrapperDto<SettingDto>>(url.getUrl(), url.getOptions())
    ).pipe(map((res) => new WrapperDto(res)));
  }

  public updateSettings(_user: UserDto, settings: SettingDto[]): Observable<WrapperDto<SettingDto>> {
    const url: UrlDto = UrlBuilder.builder().addPath(this.userSettingsUpdate).addParam('id', 'self').build();
    return this.storageService.cacheUpdate(this.userSettingStorageKey, () =>
      this.http.post<WrapperDto<SettingDto>>(url.getUrl(), settings, url.getOptions())
    ).pipe(map((res) => new WrapperDto(res)));
  }

  // ##########################################################################
  // #### Emails
  // ##########################################################################

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  public readEmails(_user: UserDto): Observable<WrapperDto<EmailDto>> {
    const url: UrlDto = UrlBuilder.builder().addPath(this.emailAllRead).addParam('id', 'self').build();
    return this.http.get<WrapperDto<EmailDto>>(url.getUrl(), url.getOptions())
      .pipe(map((res) => new WrapperDto(res)));
  }

  public createEmail(_user: UserDto, email: EmailDto): Observable<WrapperDto<EmailDto>> {
    const url: UrlDto = UrlBuilder.builder().addPath(this.emailCreate).addParam('id', 'self').build();
    return this.http.post<WrapperDto<EmailDto>>(url.getUrl(), email, url.getOptions())
      .pipe(map((res) => new WrapperDto(res)));
  }

  public deleteEmail(_user: UserDto, email: EmailDto): Observable<WrapperDto<VoidDto>> {
    const url: UrlDto = UrlBuilder.builder().addPath(this.emailDelete).addParam('id', 'self').build();
    return this.http.post<WrapperDto<VoidDto>>(url.getUrl(), email, url.getOptions())
      .pipe(map((res) => new WrapperDto(res)));
  }

  // ##########################################################################
  // #### Overview
  // ##########################################################################

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  public getOverview(_user: UserDto): Observable<WrapperDto<OverviewDto>> {
    const url: UrlDto = UrlBuilder.builder().addPath(this.overviewRead).addParam('id', 'self').build();
    return this.http.get<WrapperDto<OverviewDto>>(url.getUrl(), url.getOptions())
      .pipe(map((res) => new WrapperDto(res)));
  }

  /** Die erteilten Zustimmungen des angemeldeten Nutzers — ohne Zwischenspeicher, sie aendern sich nur bei einem Vertragsabschluss. */
  public getAgreements(): Observable<WrapperDto<AgreementDto>> {
    const url: UrlDto = UrlBuilder.builder().addPath(this.userAgreementsRead).build();
    return this.http.get<WrapperDto<AgreementDto>>(url.getUrl(), url.getOptions())
      .pipe(map((res) => new WrapperDto(res)));
  }
}
